import { useEffect, useState } from "react";

import API from "../axios";
import { getRestApiUrl } from "../utils/backendOverride";

// Server-owned navigation value that gates the "Bot Maker" entry in the
// legacy shell's menu. Mirrors `bot-maker.view` from
// apps/next/src/modules/user/constants.ts (NAVIGATION_VALUES) and the
// `navigation` array returned by `GET /api/auth/me`
// (apps/next/src/modules/user/api/get-me.ts) — see PR #316 review, B1: the
// host does not know `Company.botMakerV5IsActive` (per-company switch) or
// the `bot-maker.view` grant, only the server does, so the host asks the
// same endpoint next uses instead of guessing from the legacy
// `user.data.permissions` map.
const BOT_MAKER_NAV_VALUE = "bot-maker.view";
const ME_PATH = "/api/auth/me";

// `/api/auth/me` only exists on the new gateway (monorepo `apps/server` —
// the same backend apps/next reaches via VITE_API_URL), not on the legacy
// REST backend that `getRestApiUrl()` resolves for every other host
// request: that legacy backend 404s on this route ("Cannot GET
// /api/auth/me"), which the fail-closed catch below turns into a silently
// hidden menu entry. `REACT_APP_AGENT_GATEWAY_URL`, when set to a non-empty
// value, points this one request at the gateway instead (e.g. the staging
// Railway service in a Vercel preview whose `REACT_APP_REST_API` still
// targets prod's legacy backend); a trailing slash is stripped so the
// concatenation with ME_PATH below never doubles up. When the var is unset,
// behavior is unchanged: `getRestApiUrl()` as before.
function getMeBaseUrl() {
  const gatewayUrl = process.env.REACT_APP_AGENT_GATEWAY_URL;
  const trimmedGatewayUrl =
    typeof gatewayUrl === "string" ? gatewayUrl.trim() : "";
  const base = trimmedGatewayUrl !== "" ? trimmedGatewayUrl : getRestApiUrl();
  return typeof base === "string" && base.endsWith("/")
    ? base.slice(0, -1)
    : base;
}

// Module-level cache so every consumer shares a single `/api/auth/me`
// request per session instead of refetching on each mount — same shape as
// useFeatureFlags.js for the same reason: this is a lightweight gate on the
// legacy shell that must never block first render and must fail closed.
let cachedVisible = null;
let inFlightRequest = null;

export const parseNavigation = (data) =>
  Array.isArray(data?.navigation) ? data.navigation : [];

export const fetchBotMakerNavVisible = () => {
  if (cachedVisible !== null) return Promise.resolve(cachedVisible);
  if (!inFlightRequest) {
    // Absolute URL: the shared `API` instance's request interceptor
    // (apps/host/src/axios.js) unconditionally overwrites `config.baseURL`
    // with `getRestApiUrl()` on every call, so passing a relative path here
    // could never target the gateway independently of the rest of the
    // host's REST calls. Axios only applies `baseURL` to relative URLs, so
    // an absolute one (built from getMeBaseUrl() above) bypasses that
    // interceptor line entirely while still getting the Authorization
    // header it also attaches.
    inFlightRequest = API.get(`${getMeBaseUrl()}${ME_PATH}`)
      .then((response) => {
        const navigation = parseNavigation(response?.data);
        cachedVisible = navigation.includes(BOT_MAKER_NAV_VALUE);
        return cachedVisible;
      })
      .catch(() => {
        // Fail closed: any error (network, 401, 500, malformed body) hides
        // the entry instead of showing it. Not cached, so a later mount
        // (e.g. after the user logs back in) can retry.
        inFlightRequest = null;
        return false;
      });
  }
  return inFlightRequest;
};

// Test-only: module-level cache above must not leak between test cases.
export const __resetBotMakerNavCacheForTests = () => {
  cachedVisible = null;
  inFlightRequest = null;
};

const useBotMakerNav = () => {
  const [visible, setVisible] = useState(cachedVisible === true);

  useEffect(() => {
    let mounted = true;
    fetchBotMakerNavVisible().then((resolvedVisible) => {
      if (mounted) setVisible(resolvedVisible);
    });
    return () => {
      mounted = false;
    };
  }, []);

  return visible;
};

export default useBotMakerNav;
